import React, {
  ChangeEvent,
  MouseEvent,
  ReactElement,
  useEffect,
  useState,
} from 'react';
import {
  BlackTextEditableUsf,
  FactorInput,
  FlexEndColumn,
  OperationText,
  RowWithTopMargin,
  SignColumn,
  TextColumn,
  TitleText,
  XSmallSPWInput,
} from 'spw-components/programming-scenarios/building-hs-sc-scenario/styles';
import { isNaN, isString } from 'lodash';
import { MAX_DEPT_USF, NO_DATA_TO_SHOW } from 'spw-constants';
import { asUsf, asUsfOrZero } from 'spw-shared/utils';
import { InputAdornment } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useDispatch } from 'react-redux';
import { setTotalProgramAvailableUsf } from 'spw-redux/modules/calculators/actions';
import { colors } from 'spw-theme';
import useUnitConversion from 'spw-components/unit-conversion/hooks';

interface UsfEditableRowProps {
  leftHeader: string | ReactElement;
  middleHeader: string | ReactElement;
  rightHeader: string | ReactElement;
  totalAvailableUsf: number;
  totalProgramUsf: number | typeof NO_DATA_TO_SHOW;
  setEditFactor: (editFactor: string) => void;
  setShowFactorModal: (show: boolean) => void;
  showInputModal: boolean;
  testingIdPrefix?: string;
  showDecimals: boolean;
  disabled?: boolean;
}

const UsfEditableRow: React.FC<UsfEditableRowProps> = ({
  leftHeader,
  middleHeader,
  rightHeader,
  totalAvailableUsf,
  totalProgramUsf,
  setEditFactor,
  setShowFactorModal,
  showInputModal,
  testingIdPrefix,
  showDecimals,
  disabled,
}: UsfEditableRowProps) => {
  const dispatch = window.SPWExport ? Function.prototype : useDispatch();
  const [usf, setUsf] = useState(asUsf(totalAvailableUsf) || '');
  const { convertToOriginalUnit } = useUnitConversion();

  useEffect(() => {
    setUsf(asUsf(totalAvailableUsf) || '');
  }, [totalAvailableUsf]);

  const programDelta =
    totalProgramUsf !== NO_DATA_TO_SHOW
      ? totalAvailableUsf - totalProgramUsf
      : NO_DATA_TO_SHOW;

  const handleUsfInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    const usfValue = parseFloat(inputValue.replace(/,/g, ''));

    if (usfValue > MAX_DEPT_USF) {
      setUsf(asUsf(MAX_DEPT_USF));
    } else if (usfValue > 0) {
      setUsf(asUsf(usfValue));
    } else {
      setUsf('');
    }
  };

  const handleUsfOnBlur = () => {
    const usfValue = convertToOriginalUnit(parseFloat(usf.replace(/,/g, '')), {
      unitType: 'area',
    });

    if (isNaN(usfValue)) {
      dispatch(setTotalProgramAvailableUsf(0));
      setUsf(NO_DATA_TO_SHOW);
    } else {
      dispatch(setTotalProgramAvailableUsf(usfValue));
    }
  };

  const handleOnFocus = () => {
    if (usf === 0 || usf === NO_DATA_TO_SHOW) {
      setUsf('');
    }
  };

  return (
    <RowWithTopMargin container>
      <FlexEndColumn item xs={3}>
        <TitleText data-testid={`${testingIdPrefix}_left_label`}>
          {leftHeader}
        </TitleText>
        {showInputModal ? (
          <FactorInput
            style={{ width: '120px' }}
            InputLabelProps={{ shrink: true }}
            value={asUsf(totalAvailableUsf)}
            variant="outlined"
            disabled={disabled}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <EditIcon
                    onClick={
                      disabled
                        ? undefined
                        : (e: MouseEvent<SVGSVGElement>) => {
                            e.preventDefault();
                            setEditFactor('total-available-usf');
                            setShowFactorModal(true);
                          }
                    }
                    key="edit"
                    data-testid="edit_tps_available_usf"
                    sx={{ color: colors.mainBlue }}
                  />
                </InputAdornment>
              ),
            }}
            data-testid={`${testingIdPrefix}_left_value`}
          />
        ) : (
          <XSmallSPWInput
            value={usf}
            disabled={disabled}
            onChange={handleUsfInputChange}
            onBlur={handleUsfOnBlur}
            onFocus={handleOnFocus}
            data-testid={`${testingIdPrefix}_left_value`}
          />
        )}
      </FlexEndColumn>
      <FlexEndColumn item xs={1}>
        <SignColumn className="input">
          <OperationText style={{ fontSize: 22 }}>-</OperationText>
        </SignColumn>
      </FlexEndColumn>
      <FlexEndColumn item xs={3}>
        <TitleText data-testid={`${testingIdPrefix}_middle_label`}>
          {middleHeader}
        </TitleText>
        <TextColumn>
          <BlackTextEditableUsf data-testid={`${testingIdPrefix}_middle_value`}>
            {isNaN(totalProgramUsf) || isString(totalProgramUsf)
              ? NO_DATA_TO_SHOW
              : asUsf(totalProgramUsf, showDecimals)}{' '}
          </BlackTextEditableUsf>
        </TextColumn>
      </FlexEndColumn>
      <FlexEndColumn item xs={1}>
        <SignColumn className="input">
          <OperationText>=</OperationText>
        </SignColumn>
      </FlexEndColumn>
      <FlexEndColumn item xs={3}>
        <TitleText data-testid={`${testingIdPrefix}_right_label`}>
          {rightHeader}
        </TitleText>
        <TextColumn>
          {!isNaN(programDelta) && !isString(programDelta) ? (
            <BlackTextEditableUsf
              className={(() => {
                if (programDelta > 0) return 'positive';
                if (programDelta < 0) return 'negative';
                return undefined;
              })()}
              data-testid={`${testingIdPrefix}_right_value`}
            >
              {programDelta > 0
                ? `+${asUsf(programDelta, showDecimals)}`
                : `${asUsfOrZero(programDelta, showDecimals)}`}
            </BlackTextEditableUsf>
          ) : (
            <BlackTextEditableUsf
              data-testid={`${testingIdPrefix}_right_value`}
            >
              {NO_DATA_TO_SHOW}
            </BlackTextEditableUsf>
          )}
        </TextColumn>
      </FlexEndColumn>
    </RowWithTopMargin>
  );
};

export default UsfEditableRow;
